import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  RISK_PROFESSION_CATALOG,
  RISK_CANAL_VIA_CATALOG,
  RISK_PRODUCTO_CATALOG,
  RISK_COUNTRY_RISK_CATALOG,
  RISK_DOMICILIO_CATALOG,
  RISK_FACTOR_REFERENCE,
  INDICATOR_APPLICABILITY,
  catalogForMode,
  kycPlantilla,
  requiredDocsForPerson,
  isUboByOwnership,
  UBO_THRESHOLD_BY_SECTOR,
  ALLOWED_DOCUMENT_MIME_TYPES,
  DOC_TYPE_KEYS,
  DOC_TYPE_LABELS,
  detectDocType,
  type DocTypeKey,
  type EntityMode,
  type ExpedienteEntityType,
  type ExpedienteProposito,
  type ExpedienteFuenteFondos,
  type OwnerRol,
  type OwnerPosicion,
  type RiskInputs,
  type RiskCatalogEntry,
  formatIdNumber,
  SUPPORTED_ID_COUNTRIES,
  iso2ToIso3,
  idNumberExample,
} from '@cumpli/shared';
import { api, RequestError } from '../lib/api';
import { useToast } from '../lib/use-toast';
import { useDemo } from '../lib/demo-tour';
import { useExpedientes, useTenantSettings } from '../lib/hooks';
import { loadDraft, saveDraft, clearDraft, isMeaningful, type DraftPerson } from '../lib/draft';
import { formatRuc, normalizeDocId, looksValidDocId } from '../lib/format';
import type { NewExpedienteInput, CreateOwnerInput } from '../lib/phase1';
import {
  FlowShell,
  QuestionScreen,
  ChoiceCard,
  BigInput,
  Celebrate,
  FieldHelp,
  Toggle,
  FlowSelect,
  type FlowOption,
} from '../components/flow';
import { DocSlots } from '../components/doc-slots';
import { cn } from '../components/ui';
import {
  User,
  Building2,
  Landmark,
  Camera,
  Paperclip,
  UploadCloud,
  FileQuestion,
  FileCheck2,
  X,
  Briefcase,
  PiggyBank,
  Hammer,
  Users,
  ScrollText,
  Plus,
  Pencil,
  Trash2,
} from 'lucide-react';

// ---------------------------------------------------------------- helpers ----

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Lenient email check — the backend (emailSchema) is the source of truth. */
const looksValidEmail = (v: string): boolean =>
  v.trim() === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

const toOpts = (c: readonly RiskCatalogEntry[]): FlowOption[] =>
  c.map((e) => ({ value: e.key, label: e.label }));

const NACIONALIDAD_OPTS = toOpts(RISK_COUNTRY_RISK_CATALOG);
const DOMICILIO_OPTS = toOpts(RISK_DOMICILIO_CATALOG);
const COUNTRY_LABEL = new Map(RISK_COUNTRY_RISK_CATALOG.map((e) => [e.key, e.label] as const));

const PROPOSITO_OPTS: FlowOption[] = [
  { value: 'tenedora_acciones', label: 'Tenedora de acciones' },
  { value: 'tenedora_cuenta_bancaria', label: 'Cuenta bancaria' },
  { value: 'planeacion_patrimonial', label: 'Planeación patrimonial' },
  { value: 'inversiones', label: 'Inversiones' },
  { value: 'actividad_comercial', label: 'Actividad comercial' },
  { value: 'tenedora_activos', label: 'Tenedora de activos' },
  { value: 'otros', label: 'Otros' },
];

const FUENTE_FONDOS_OPTS: FlowOption[] = [
  { value: 'fondos_negocios', label: 'Negocios' },
  { value: 'fondos_personales', label: 'Personales' },
  { value: 'donacion', label: 'Donación' },
  { value: 'prestamo', label: 'Préstamo' },
  { value: 'contribucion_accionistas', label: 'Contribución de accionistas' },
  { value: 'herencia', label: 'Herencia' },
  { value: 'dividendos', label: 'Dividendos' },
  { value: 'otros', label: 'Otros' },
];

const TIPO_DOC_OPTS: FlowOption[] = [
  { value: 'cedula', label: 'Cédula (Panamá)' },
  { value: 'pasaporte', label: 'Pasaporte (extranjero)' },
];

const SOCIEDAD_ROLES: FlowOption[] = [
  { value: 'director', label: 'Director' },
  { value: 'dignatario', label: 'Dignatario' },
  { value: 'accionista', label: 'Accionista' },
  { value: 'beneficiario_final', label: 'Beneficiario final' },
];
const FUNDACION_ROLES: FlowOption[] = [
  { value: 'fundador', label: 'Fundador' },
  { value: 'consejo_fundacional', label: 'Consejo fundacional' },
  { value: 'beneficiario_final', label: 'Beneficiario final' },
];
const POSICION_OPTS: FlowOption[] = [
  { value: 'primario', label: 'Primario' },
  { value: 'secundario', label: 'Secundario (sin requisitos documentales)' },
];

const PERFIL: FlowOption[] = [
  { value: 'Asalariado', label: 'Asalariado', /* icon set below */ },
  { value: 'Jubilado', label: 'Jubilado' },
  { value: 'Independiente', label: 'Independiente' },
];
const PERFIL_ICON = { Asalariado: Briefcase, Jubilado: PiggyBank, Independiente: Hammer } as const;

/** Short plain-language `explicacion` per matrix factor, keyed `family[.subfactor]`. */
const RISK_HELP: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const fam of RISK_FACTOR_REFERENCE) {
    m[fam.key] = fam.explicacion;
    for (const sub of fam.subfactors) m[`${fam.key}.${sub.key}`] = sub.explicacion;
  }
  return m;
})();

const labelOf = (options: FlowOption[], value: string): string =>
  options.find((o) => o.value === value)?.label ?? '—';

// ---------------------------------------------------------------- types ----

type EntityType = ExpedienteEntityType;

type PersonDocMap = Partial<Record<DocTypeKey, File>>;

interface PersonForm {
  localId: string;
  rol: OwnerRol;
  posicion: '' | OwnerPosicion;
  name: string;
  tipoDocumento: 'cedula' | 'pasaporte';
  numeroDocumento: string;
  nacionalidad: string;
  paisResidenciaFiscal: string;
  ownershipPct: string;
  porcentajePatrimonial: string;
  pepPropio: boolean;
  pepFamiliar: boolean;
  usPerson: boolean;
}

const defaultRol = (et: EntityType): OwnerRol => (et === 'fundacion' ? 'fundador' : 'director');

function blankPerson(et: EntityType): PersonForm {
  return {
    localId: crypto.randomUUID(),
    rol: defaultRol(et),
    posicion: '',
    name: '',
    tipoDocumento: 'cedula',
    numeroDocumento: '',
    nacionalidad: '',
    paisResidenciaFiscal: '',
    ownershipPct: '',
    porcentajePatrimonial: '',
    pepPropio: false,
    pepFamiliar: false,
    usPerson: false,
  };
}

const toDraftPerson = (p: PersonForm): DraftPerson => ({ ...p, posicion: p.posicion });
const fromDraftPerson = (d: DraftPerson): PersonForm => ({
  localId: d.localId,
  rol: d.rol as OwnerRol,
  posicion: (d.posicion as '' | OwnerPosicion) ?? '',
  name: d.name,
  tipoDocumento: d.tipoDocumento === 'pasaporte' ? 'pasaporte' : 'cedula',
  numeroDocumento: d.numeroDocumento,
  nacionalidad: d.nacionalidad,
  paisResidenciaFiscal: d.paisResidenciaFiscal,
  ownershipPct: d.ownershipPct,
  porcentajePatrimonial: d.porcentajePatrimonial,
  pepPropio: d.pepPropio,
  pepFamiliar: d.pepFamiliar,
  usPerson: d.usPerson,
});

type StepKey =
  | 'documentos'
  | 'tipo'
  | 'escanear'
  | 'datosPn'
  | 'kycPn'
  | 'canalPn'
  | 'domicilio'
  | 'docsPn'
  | 'datosPj'
  | 'personas'
  | 'riesgoPj'
  | 'docsPj'
  | 'revision';

// El sector (tipo de sujeto obligado) ya no es un paso del asistente: se lee del
// tenant y se cambia desde la Matriz de riesgo; aquí solo poda catálogos y docs.
// 'documentos' es SIEMPRE el primer paso (opcional): soltar el KYC completo ahí
// autollena identidad (OCR de la cédula), tipo de cliente y ranuras documentales.
// 'domicilio' captura la dirección (OCR de recibo de servicio) — para PJ también
// alimenta direccionFisica; el autollenado de 'documentos' nunca la escribe
// directamente, así que este paso sigue siendo la fuente confirmable por el usuario.
const PN_STEPS: StepKey[] = [
  'documentos',
  'tipo',
  'escanear',
  'datosPn',
  'kycPn',
  'canalPn',
  'domicilio',
  'docsPn',
  'revision',
];
const PJ_STEPS: StepKey[] = [
  'documentos',
  'tipo',
  'datosPj',
  'personas',
  'riesgoPj',
  'domicilio',
  'docsPj',
  'revision',
];

/** Un archivo soltado en el paso inicial + el tipo detectado (null = sin clasificar). */
interface IntakeFile {
  id: string;
  file: File;
  dt: DocTypeKey | null;
}

/** Marcadores documentales que delatan una persona jurídica / fundación. */
const PJ_MARKERS: readonly DocTypeKey[] = [
  'pacto_social',
  'certificados_acciones',
  'registro_accionistas',
  'declaracion_jurada_bf',
  'ruc_aviso_operacion',
  'poder_acta_junta',
  'organigrama_estructura',
];

const INTAKE_TYPE_OPTS: FlowOption[] = DOC_TYPE_KEYS.map((k) => ({
  value: k,
  label: DOC_TYPE_LABELS[k],
}));

// ---------------------------------------------------------------- page ----

export default function ExpedienteNuevo() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const demo = useDemo();
  const toast = useToast();
  const { data: exps } = useExpedientes();
  const { data: tenantSettings } = useTenantSettings();

  // D1 — the sector is a TENANT attribute (read-only here). Falls back to
  // 'financiero' (the API/demo default) while the setting loads.
  const sector: EntityMode = tenantSettings?.entityMode ?? 'financiero';

  const draft = useMemo(() => {
    if (demo.playing) return null;
    const d = loadDraft();
    return isMeaningful(d) ? d : null;
  }, []);

  // Restaurar por CLAVE de paso, no por índice: el array de pasos cambia de forma
  // ('documentos' antepuesto; 'escanear' se elimina tras el OCR inicial), así que un
  // índice posicional guardado puede aterrizar en el paso equivocado.
  const [idx, setIdx] = useState(() => {
    if (!draft?.stepKey) return 0;
    const base =
      draft.entityType === 'sociedad_anonima' || draft.entityType === 'fundacion'
        ? PJ_STEPS
        : PN_STEPS;
    return Math.max(0, base.indexOf(draft.stepKey as StepKey));
  });
  const [busy, setBusy] = useState(false);
  const [doneName, setDoneName] = useState<string | null>(null);
  const [doneId, setDoneId] = useState<string | null>(null);

  const [entityType, setEntityType] = useState<EntityType>(
    (draft?.entityType as EntityType) ?? 'persona_natural'
  );

  // Identidad (PN) / entidad (PJ).
  const [name, setName] = useState(draft?.name ?? '');
  const [docId, setDocId] = useState(draft?.docId ?? '');
  // ISO 3166-1 alpha-3 — country whose ID-number format applies (natural
  // persons only). Defaults to Panamá; OCR can override it via a detected
  // `res.fields.country`.
  const [country, setCountry] = useState(draft?.country ?? 'PAN');
  const [birthDate, setBirthDate] = useState(draft?.birthDate ?? '');
  const [perfil, setPerfil] = useState(draft?.perfil ?? 'Asalariado');
  const [email, setEmail] = useState(draft?.email ?? '');
  const [phone, setPhone] = useState(draft?.phone ?? '');
  const [direccion, setDireccion] = useState(draft?.direccion ?? '');

  // Persona jurídica — datos de la entidad.
  const [folio, setFolio] = useState(draft?.folio ?? '');
  const [constitutionDate, setConstitutionDate] = useState(draft?.constitutionDate ?? '');
  const [paisesActividad, setPaisesActividad] = useState<string[]>(draft?.paisesActividad ?? []);
  const [paisActividadPick, setPaisActividadPick] = useState('');

  // KYC núcleo + insumos de matriz (claves de catálogo).
  const [proposito, setProposito] = useState(draft?.proposito ?? '');
  const [profesion, setProfesion] = useState(draft?.profesion ?? '');
  const [fuenteFondos, setFuenteFondos] = useState(draft?.fuenteFondos ?? '');
  const [nacionalidad, setNacionalidad] = useState(draft?.nacionalidad ?? '');
  const [paisResidenciaFiscal, setPaisResidenciaFiscal] = useState(draft?.paisResidenciaFiscal ?? '');
  const [paisDomicilio, setPaisDomicilio] = useState(draft?.paisDomicilio ?? '');
  const [canalVia, setCanalVia] = useState(draft?.canalVia ?? '');
  const [producto, setProducto] = useState(draft?.producto ?? '');
  const [pepPropio, setPepPropio] = useState(draft?.pepPropio ?? false);
  const [pepFamiliar, setPepFamiliar] = useState(draft?.pepFamiliar ?? false);
  const [usPerson, setUsPerson] = useState(draft?.usPerson ?? false);
  const [multiplesNacionalidades, setMultiplesNacionalidades] = useState(
    draft?.multiplesNacionalidades ?? false
  );

  // Personas relacionadas (Sociedad / Fundación) + sus documentos (Files, no draft).
  const [people, setPeople] = useState<PersonForm[]>(
    () => draft?.people?.map(fromDraftPerson) ?? []
  );
  const [personDocs, setPersonDocs] = useState<Record<string, PersonDocMap>>({});
  const [editorPerson, setEditorPerson] = useState<PersonForm | null>(null);
  const [editorDocs, setEditorDocs] = useState<PersonDocMap>({});

  // Documentos de la entidad (Files, no draft).
  const [entityDocs, setEntityDocs] = useState<PersonDocMap>({});

  // OCR id-scan state.
  const [scanBusy, setScanBusy] = useState(false);
  const [scanError, setScanError] = useState<'generic' | 'tooLarge' | null>(null);
  const [scanHint, setScanHint] = useState<'ok' | 'low' | null>(null);
  const [scanDragOver, setScanDragOver] = useState(false);

  // Paso inicial 'documentos' (opcional): archivos soltados + qué se autollenó.
  const [intakeFiles, setIntakeFiles] = useState<IntakeFile[]>([]);
  const [intakeDrag, setIntakeDrag] = useState(false);
  const [intakeBusy, setIntakeBusy] = useState(false);
  // La identidad ya se leyó de la cédula soltada → el paso 'escanear' sobra.
  const [intakeOcrDone, setIntakeOcrDone] = useState(false);

  // Progreso del guardado — reanudable: si createExpediente ya corrió y un paso
  // posterior falló, el reintento NO vuelve a crear el expediente (evita duplicados).
  const submitProgress = useRef<{
    created: { id: string; name: string } | null;
    owners: Record<string, string>; // localId → ownerId ya creado
    uploaded: Set<string>; // `${localId|entity}:${docType}` ya subido
  }>({ created: null, owners: {}, uploaded: new Set() });

  // OCR recibo-de-servicio scan state (domicilio step) — kept separate from the
  // cédula scan state above since both can coexist across steps.
  const [reciboBusy, setReciboBusy] = useState(false);
  const [reciboError, setReciboError] = useState<'generic' | 'tooLarge' | null>(null);

  const isPn = entityType === 'persona_natural';
  const isFundacion = entityType === 'fundacion';
  const showProducto = INDICATOR_APPLICABILITY[sector].families.includes('producto');

  const PROFESION_OPTS = useMemo(() => toOpts(catalogForMode(RISK_PROFESSION_CATALOG, sector)), [sector]);
  const CANAL_OPTS = useMemo(() => toOpts(catalogForMode(RISK_CANAL_VIA_CATALOG, sector)), [sector]);
  const PRODUCTO_OPTS = useMemo(() => toOpts(catalogForMode(RISK_PRODUCTO_CATALOG, sector)), [sector]);
  const ROLE_OPTS = isFundacion ? FUNDACION_ROLES : SOCIEDAD_ROLES;

  const baseSteps = isPn ? PN_STEPS : PJ_STEPS;
  // Si la cédula ya se leyó en el paso inicial, escanear de nuevo no aporta.
  const steps = intakeOcrDone ? baseSteps.filter((s) => s !== 'escanear') : baseSteps;
  const safeIdx = Math.min(idx, steps.length - 1);
  const key = steps[safeIdx]!;
  const stepLabel = `Paso ${safeIdx + 1} de ${steps.length}`;
  const progress = safeIdx / (steps.length - 1);

  // Autosave the serializable part of the draft (skip during the demo).
  useEffect(() => {
    if (demo.playing || doneName) return;
    saveDraft({
      entityType,
      idx,
      stepKey: key,
      name,
      docId,
      country,
      birthDate,
      perfil,
      email,
      phone,
      direccion,
      folio,
      constitutionDate,
      paisesActividad,
      proposito,
      profesion,
      fuenteFondos,
      nacionalidad,
      paisResidenciaFiscal,
      paisDomicilio,
      canalVia,
      producto,
      pepPropio,
      pepFamiliar,
      usPerson,
      multiplesNacionalidades,
      people: people.map(toDraftPerson),
    });
  }, [
    demo.playing,
    doneName,
    entityType,
    idx,
    name,
    docId,
    country,
    birthDate,
    perfil,
    email,
    phone,
    direccion,
    folio,
    constitutionDate,
    paisesActividad,
    proposito,
    profesion,
    fuenteFondos,
    nacionalidad,
    paisResidenciaFiscal,
    paisDomicilio,
    canalVia,
    producto,
    pepPropio,
    pepFamiliar,
    usPerson,
    multiplesNacionalidades,
    people,
  ]);

  function next() {
    setIdx((i) => Math.min(i + 1, steps.length - 1));
  }
  function back() {
    if (idx === 0) return navigate('/inicio');
    // Si hubo un guardado parcial y el usuario vuelve a editar, el reintento ya no
    // es válido (este flujo no tiene update de expediente/owners): se descarta el
    // progreso; el duplicado se ataja con la verificación de cédula/RUC del paso
    // de datos. // ponytail: el ref vive en memoria — un reload tras fallo parcial
    // también reinicia; el guard de cédula duplicada es la red en ambos casos.
    if (key === 'revision' && submitProgress.current.created) {
      submitProgress.current = { created: null, owners: {}, uploaded: new Set() };
    }
    setIdx((i) => i - 1);
  }

  /** OCR de la cédula → autollenar identidad. Compartido por 'documentos' y 'escanear'. */
  async function applyOcr(file: File): Promise<void> {
    const res = await api.ocrPrefill(file);
    if (res.fields.fullName) setName(res.fields.fullName);
    // Determine the citizenship country FIRST (guard against an unknown
    // ISO3 from OCR) so the scanned id number is formatted with it, not a
    // hardcoded Panamá default.
    const detected = res.fields.country;
    const detectedCountry =
      detected && SUPPORTED_ID_COUNTRIES.some((c) => c.iso3 === detected) ? detected : undefined;
    if (detectedCountry) setCountry(detectedCountry);
    if (res.fields.idNumber) setDocId(formatIdNumber(detectedCountry ?? country, res.fields.idNumber));
    if (res.fields.birthDate) setBirthDate(res.fields.birthDate);
    setScanHint(res.lowConfidence ? 'low' : 'ok');
  }

  async function handleScan(file: File) {
    setScanBusy(true);
    setScanError(null);
    try {
      await applyOcr(file);
      next();
    } catch (err) {
      setScanError(err instanceof RequestError && err.code === 'FILE_TOO_LARGE' ? 'tooLarge' : 'generic');
    } finally {
      setScanBusy(false);
    }
  }

  // ---- paso inicial 'documentos': clasificar, autollenar y mostrar todo ----

  /**
   * Cada archivo soltado se clasifica por nombre y se engancha en su ranura;
   * la mezcla de documentos infiere el tipo de cliente; y si viene la cédula,
   * el OCR autollena la identidad — todo visible en la misma pantalla.
   */
  async function ingestIntake(list: FileList | File[]) {
    const items: IntakeFile[] = Array.from(list)
      .filter((f) => (ALLOWED_DOCUMENT_MIME_TYPES as readonly string[]).includes(f.type))
      .map((f) => ({ id: crypto.randomUUID(), file: f, dt: detectDocType(f.name) }));
    if (items.length === 0) return;
    // Functional update — dos drops seguidos antes del re-render no pierden el primero.
    setIntakeFiles((prev) => [...prev, ...items]);
    const all = [...intakeFiles, ...items];

    // Engancha los clasificados en las ranuras de la entidad (primero gana).
    setEntityDocs((prev) => {
      const nx = { ...prev };
      for (const it of items) if (it.dt && nx[it.dt] === undefined) nx[it.dt] = it.file;
      return nx;
    });

    // El tipo de cliente se infiere de la mezcla documental (confirmable después).
    const dts = new Set(all.map((i) => i.dt).filter(Boolean) as DocTypeKey[]);
    const esPj = PJ_MARKERS.some((k) => dts.has(k));
    if (dts.has('acta_fundacional')) setEntityType('fundacion');
    else if (esPj) setEntityType('sociedad_anonima');

    // Con cédula soltada y sin señales de PJ → OCR autollena la identidad (PN).
    const ced = items.find((i) => i.dt === 'cedula_pasaporte');
    if (ced && !esPj && !dts.has('acta_fundacional')) {
      setIntakeBusy(true);
      try {
        await applyOcr(ced.file);
        setIntakeOcrDone(true);
      } catch {
        // Opcional por diseño: si el OCR falla, el paso 'escanear' sigue disponible.
      } finally {
        setIntakeBusy(false);
      }
    }
  }

  /** Reasignar (o clasificar a mano) un archivo del paso inicial. */
  function reassignIntake(id: string, dt: DocTypeKey) {
    const item = intakeFiles.find((i) => i.id === id);
    if (!item) return;
    setIntakeFiles((prev) => prev.map((i) => (i.id === id ? { ...i, dt } : i)));
    setEntityDocs((prev) => {
      const nx = { ...prev };
      // Suelta la ranura anterior si apuntaba a este mismo archivo.
      if (item.dt && nx[item.dt] === item.file) delete nx[item.dt];
      nx[dt] = item.file;
      return nx;
    });
  }

  function removeIntake(id: string) {
    const item = intakeFiles.find((i) => i.id === id);
    if (!item) return;
    setIntakeFiles((prev) => prev.filter((i) => i.id !== id));
    setEntityDocs((prev) => {
      const nx = { ...prev };
      if (item.dt && nx[item.dt] === item.file) {
        // Si hay otro archivo del mismo tipo en la lista, asciende a la ranura.
        const twin = intakeFiles.find((i) => i.id !== id && i.dt === item.dt);
        if (twin) nx[item.dt] = twin.file;
        else delete nx[item.dt];
      }
      return nx;
    });
  }

  // OCR-prefill de un recibo de servicio para el domicilio — no avanza el paso
  // automáticamente: el campo editable vive en la misma pantalla y es requerido.
  async function handleReciboScan(file: File) {
    setReciboBusy(true);
    setReciboError(null);
    try {
      const res = await api.ocrPrefill(file, 'address');
      if (res.fields.direccion) setDireccion(res.fields.direccion);
    } catch (err) {
      setReciboError(err instanceof RequestError && err.code === 'FILE_TOO_LARGE' ? 'tooLarge' : 'generic');
    } finally {
      setReciboBusy(false);
    }
  }

  // Estructura de la matriz (cliente.estructura) derivada del tipo de entidad.
  const estructura: string = isPn
    ? perfil === 'Independiente'
      ? 'pn_independiente'
      : 'pn_asalariada'
    : isFundacion
      ? 'fundacion'
      : 'sa_nominativa';

  const anyPersonPep = people.some((p) => p.pepPropio || p.pepFamiliar);
  const pepOverride = isPn ? pepPropio || pepFamiliar : anyPersonPep;

  function buildRiskInputs(): RiskInputs {
    return {
      estructura,
      ...(profesion ? { profesion } : {}),
      ...(nacionalidad ? { nacionalidad } : {}),
      ...(paisDomicilio ? { domicilio: paisDomicilio } : {}),
      ...(canalVia ? { canal_via: canalVia } : {}),
      ...(showProducto && producto ? { producto } : {}),
      ...(pepOverride ? { pep: true } : {}),
    };
  }

  function buildExpedientePayload(): NewExpedienteInput {
    const kind = isPn
      ? `Persona Natural · ${perfil}`
      : isFundacion
        ? 'Persona Jurídica · Fundación de Interés Privado'
        : 'Persona Jurídica · Sociedad Anónima';
    const profesionLabel = profesion ? labelOf(PROFESION_OPTS, profesion) : '';
    const sectorText = (profesionLabel || t(`entityMode.${sector}`) || 'General').slice(0, 120);
    return {
      persona: isPn ? 'natural' : 'legal',
      name: name.trim(),
      docId: docId.trim(),
      sector: sectorText,
      kind,
      entityType,
      riskInputs: buildRiskInputs(),
      ...(email.trim() ? { email: email.trim() } : {}),
      ...(phone.trim() ? { phone: phone.trim() } : {}),
      ...(isPn && birthDate ? { dateOfBirth: birthDate } : {}),
      ...(direccion.trim() ? { direccion: direccion.trim() } : {}),
      ...(proposito ? { proposito: proposito as ExpedienteProposito } : {}),
      ...(paisesActividad.length ? { paisesActividad } : {}),
      ...(fuenteFondos ? { fuenteFondos: fuenteFondos as ExpedienteFuenteFondos } : {}),
      // Persona Jurídica — folio, fecha de constitución y dirección física (Ley 23
      // domicilio real). El paso "domicilio" (compartido con PN) es el único punto
      // de captura de dirección; para PJ su valor también alimenta `direccionFisica`
      // para que el campo Ley 23 de #306 se siga registrando sin pedirlo dos veces.
      ...(!isPn
        ? {
            ...(direccion.trim() ? { direccionFisica: direccion.trim() } : {}),
            ...(folio.trim() ? { folio: folio.trim() } : {}),
            ...(constitutionDate ? { constitutionDate } : {}),
          }
        : {}),
      // Persona Natural (principal) — declaraciones FATCA/CRS. Los booleanos se
      // envían sin condición: los toggles siempre se presentan, así que `false`
      // es una declaración real, no una omisión.
      ...(isPn
        ? {
            usPerson,
            multiplesNacionalidades,
            ...(paisResidenciaFiscal ? { paisResidenciaFiscal } : {}),
          }
        : {}),
    };
  }

  function buildOwnerPayload(p: PersonForm): CreateOwnerInput {
    const pct = Number(p.ownershipPct) || 0;
    const patr = p.porcentajePatrimonial.trim() ? Number(p.porcentajePatrimonial) || 0 : undefined;
    const subjectRef = (p.numeroDocumento.trim() || p.name.trim()).slice(0, 50);
    return {
      subjectRef,
      name: p.name.trim(),
      ownerType: 'natural',
      ownershipPct: pct,
      rol: p.rol,
      pepPropio: p.pepPropio,
      pepFamiliar: p.pepFamiliar,
      usPerson: p.usPerson,
      ...(p.posicion ? { posicion: p.posicion } : {}),
      ...(p.numeroDocumento.trim() ? { idNumber: p.numeroDocumento.trim() } : {}),
      ...(p.nacionalidad ? { country: COUNTRY_LABEL.get(p.nacionalidad) ?? p.nacionalidad } : {}),
      ...(p.paisResidenciaFiscal ? { paisResidenciaFiscal: p.paisResidenciaFiscal } : {}),
      ...(patr !== undefined ? { porcentajePatrimonial: patr } : {}),
    };
  }

  async function submit() {
    setBusy(true);
    const prog = submitProgress.current;
    try {
      if (!prog.created) {
        const created = await api.createExpediente(buildExpedientePayload());
        prog.created = { id: created.id, name: created.name };
      }
      const expId = prog.created.id;
      // People first — their documents need the created owner's id.
      for (const p of people) {
        let ownerId = prog.owners[p.localId];
        if (ownerId === undefined) {
          const owner = await api.createOwner(expId, buildOwnerPayload(p));
          ownerId = owner.id;
          prog.owners[p.localId] = ownerId;
        }
        const pdocs = personDocs[p.localId] ?? {};
        for (const dt of Object.keys(pdocs) as DocTypeKey[]) {
          const file = pdocs[dt];
          const mark = `${p.localId}:${dt}`;
          if (!file || prog.uploaded.has(mark)) continue;
          await api.uploadDocument(expId, { file, category: DOC_TYPE_LABELS[dt], ownerId });
          prog.uploaded.add(mark);
        }
      }
      // Entity-level documents (owner_id absent).
      for (const dt of Object.keys(entityDocs) as DocTypeKey[]) {
        const file = entityDocs[dt];
        const mark = `entity:${dt}`;
        if (!file || prog.uploaded.has(mark)) continue;
        await api.uploadDocument(expId, { file, category: DOC_TYPE_LABELS[dt] });
        prog.uploaded.add(mark);
      }
      await qc.invalidateQueries({ queryKey: ['expedientes'] });
      clearDraft();
      setDoneName(prog.created.name);
      setDoneId(expId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'error desconocido';
      toast({
        kind: 'error',
        message: prog.created
          ? `No se pudo guardar todo: ${msg}. Pulsa «Crear cliente» de nuevo para completar lo pendiente (sin cambiar datos entre intentos).`
          : `No se pudo guardar: ${msg}`,
      });
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    clearDraft();
    submitProgress.current = { created: null, owners: {}, uploaded: new Set() };
    setDoneName(null);
    setDoneId(null);
    setIdx(0);
    setEntityType('persona_natural');
    setName('');
    setDocId('');
    setCountry('PAN');
    setBirthDate('');
    setPerfil('Asalariado');
    setEmail('');
    setPhone('');
    setDireccion('');
    setFolio('');
    setConstitutionDate('');
    setPaisesActividad([]);
    setProposito('');
    setProfesion('');
    setFuenteFondos('');
    setNacionalidad('');
    setPaisResidenciaFiscal('');
    setPaisDomicilio('');
    setCanalVia('');
    setProducto('');
    setPepPropio(false);
    setPepFamiliar(false);
    setUsPerson(false);
    setMultiplesNacionalidades(false);
    setPeople([]);
    setPersonDocs({});
    setEditorPerson(null);
    setEditorDocs({});
    setEntityDocs({});
    setScanBusy(false);
    setScanError(null);
    setScanHint(null);
    setIntakeFiles([]);
    setIntakeDrag(false);
    setIntakeBusy(false);
    setIntakeOcrDone(false);
    setReciboBusy(false);
    setReciboError(null);
  }

  // ---- self-running demo: auto-fill a persona-natural intake ----
  const demoActive = demo.playing && demo.sceneKey === 'intake';
  useEffect(() => {
    if (!demoActive) return;
    let cancelled = false;
    const D = {
      name: 'María González Pérez',
      docId: '8-712-3456',
      birthDate: '1988-03-14',
    };
    const type = async (setter: (v: string) => void, text: string, ms: number) => {
      for (let i = 1; i <= text.length; i++) {
        if (cancelled) return;
        setter(text.slice(0, i));
        await sleep(ms);
      }
    };
    void (async () => {
      reset();
      setEntityType('persona_natural');
      // Índices por clave (PN_STEPS ahora abre con 'documentos'), no números mágicos.
      const S = (k: StepKey) => PN_STEPS.indexOf(k);
      setIdx(S('documentos'));
      await sleep(1300);
      if (cancelled) return;
      setIdx(S('tipo'));
      await sleep(1200);
      if (cancelled) return;
      setIdx(S('escanear'));
      await sleep(1400);
      if (cancelled) return;
      setIdx(S('datosPn'));
      await type(setName, D.name, 80);
      await sleep(400);
      if (cancelled) return;
      await type(setDocId, D.docId, 100);
      setBirthDate(D.birthDate);
      await sleep(900);
      if (cancelled) return;
      setIdx(S('kycPn'));
      setProposito('tenedora_cuenta_bancaria');
      setProfesion('asalariado');
      setFuenteFondos('fondos_personales');
      setNacionalidad('pa');
      setPaisResidenciaFiscal('pa');
      setPaisDomicilio('pa');
      await sleep(1600);
      if (cancelled) return;
      setIdx(S('canalPn'));
      setCanalVia('presencial');
      setProducto('cuenta_basica');
      await sleep(1400);
      if (cancelled) return;
      setIdx(S('domicilio'));
      setDireccion('Calle 50, Ciudad de Panamá');
      await sleep(1500);
      if (cancelled) return;
      setIdx(S('docsPn'));
      await sleep(1500);
      if (cancelled) return;
      setIdx(S('revision'));
      await sleep(1800);
      if (cancelled) return;
      const created = await api.createExpediente({
        persona: 'natural',
        name: D.name,
        docId: D.docId,
        sector: 'Asalariado / jubilado (ingreso verificable)',
        kind: 'Persona Natural · Asalariado',
        dateOfBirth: D.birthDate,
        direccion: 'Calle 50, Ciudad de Panamá',
        entityType: 'persona_natural',
        riskInputs: {
          estructura: 'pn_asalariada',
          profesion: 'asalariado',
          nacionalidad: 'pa',
          domicilio: 'pa',
          canal_via: 'presencial',
          producto: 'cuenta_basica',
        },
        proposito: 'tenedora_cuenta_bancaria',
        fuenteFondos: 'fondos_personales',
      });
      await qc.invalidateQueries({ queryKey: ['expedientes'] });
      if (cancelled) return;
      setDoneName(created.name);
      await sleep(2600);
      if (cancelled) return;
      demo.sceneDone();
    })();
    return () => {
      cancelled = true;
    };
  }, [demoActive]);

  // ---- completion ----
  if (doneName) {
    return (
      <FlowShell>
        <Celebrate
          title={`¡Listo! Registraste a ${doneName}`}
          message="El expediente está listo. El siguiente paso es ejecutar las validaciones y enviar a revisión desde la página del cliente."
          primaryLabel="Ver expediente"
          onPrimary={() => navigate(`/expedientes/${doneId}`)}
          secondaryLabel="Registrar otro cliente"
          onSecondary={reset}
        />
      </FlowShell>
    );
  }

  // ---- shared derivations for gating ----
  const persona = isPn ? 'natural' : 'legal';
  const dup =
    docId.trim() !== ''
      ? (exps ?? []).find((x) => normalizeDocId(x.docId) === normalizeDocId(docId))
      : undefined;
  const docHint = docId.trim() !== '' && !dup && !looksValidDocId(docId, persona);
  const emailInvalid = !looksValidEmail(email);

  function personValid(p: PersonForm): boolean {
    if (isFundacion && p.rol === 'beneficiario_final' && p.posicion === 'secundario') {
      return p.name.trim() !== '';
    }
    return p.name.trim() !== '' && p.numeroDocumento.trim() !== '';
  }

  const canNext =
    (key === 'documentos' && !intakeBusy) ||
    (key === 'tipo') ||
    (key === 'escanear') ||
    (key === 'datosPn' && name.trim() !== '' && docId.trim() !== '' && !dup) ||
    (key === 'kycPn' &&
      !!proposito &&
      !!profesion &&
      !!fuenteFondos &&
      !!nacionalidad &&
      !!paisDomicilio) ||
    (key === 'canalPn' && !!canalVia && (!showProducto || !!producto)) ||
    (key === 'domicilio' && direccion.trim() !== '') ||
    (key === 'docsPn') ||
    (key === 'datosPj' &&
      name.trim() !== '' &&
      docId.trim() !== '' &&
      !dup &&
      !!proposito &&
      !!fuenteFondos &&
      !!nacionalidad) ||
    (key === 'personas' && editorPerson === null && people.length > 0) ||
    (key === 'riesgoPj' && !!canalVia && (!showProducto || !!producto)) ||
    (key === 'docsPj') ||
    key === 'revision';

  const onEnter = (e: { key: string }) => {
    if (e.key === 'Enter' && canNext) next();
  };

  const prefillHint =
    scanHint === 'low' ? (
      <p className="mt-3 rounded-xl border border-tone-amber/40 bg-tone-amber-bg px-4 py-3 text-sm font-semibold text-tone-amber">
        {t('intakeScan.successHint')}
      </p>
    ) : scanHint === 'ok' ? (
      <p className="mt-3 text-sm text-ink-faint">{t('intakeScan.successHint')}</p>
    ) : null;

  const scanPickerCn = (base: string) => cn(base, scanBusy && 'pointer-events-none opacity-60');

  const sectorLabel = t(`entityMode.${sector}`);

  // ---- person editor handlers ----
  function openNewPerson() {
    setEditorPerson(blankPerson(entityType));
    setEditorDocs({});
  }
  function openEditPerson(p: PersonForm) {
    setEditorPerson({ ...p });
    setEditorDocs({ ...(personDocs[p.localId] ?? {}) });
  }
  function patchEditor(patch: Partial<PersonForm>) {
    setEditorPerson((prev) => (prev ? { ...prev, ...patch } : prev));
  }
  function attachEditorDoc(file: File, dt: DocTypeKey) {
    setEditorDocs((prev) => ({ ...prev, [dt]: file }));
  }
  function savePerson() {
    if (!editorPerson) return;
    const p = editorPerson;
    setPeople((prev) => {
      const exists = prev.some((x) => x.localId === p.localId);
      return exists ? prev.map((x) => (x.localId === p.localId ? p : x)) : [...prev, p];
    });
    setPersonDocs((prev) => ({ ...prev, [p.localId]: editorDocs }));
    setEditorPerson(null);
    setEditorDocs({});
  }
  function removePerson(localId: string) {
    setPeople((prev) => prev.filter((x) => x.localId !== localId));
    setPersonDocs((prev) => {
      const { [localId]: _drop, ...rest } = prev;
      return rest;
    });
  }

  const primaryLabelPersonas = editorPerson ? 'Guardar persona' : 'Continuar';
  const primaryDisabledPersonas = editorPerson
    ? !personValid(editorPerson)
    : people.length === 0;

  // Plantilla KYC efectiva — se arma sola con el sector del tenant + el tipo de
  // cliente + condiciones ya respondidas en el asistente (PEP, US person, extranjero).
  const plantilla = kycPlantilla({
    sector,
    entityType,
    condiciones: {
      pep: pepOverride,
      usPerson: isPn ? usPerson : people.some((p) => p.usPerson),
      extranjero: nacionalidad !== '' && nacionalidad !== 'pa',
    },
  });

  const beneficiarios = people.filter(
    (p) =>
      p.rol === 'beneficiario_final' ||
      (entityType === 'sociedad_anonima' &&
        p.rol === 'accionista' &&
        isUboByOwnership(Number(p.ownershipPct) || 0, sector))
  );

  return (
    <FlowShell onBack={back} progress={progress} showActions={false}>
      {/* ------------------------------------------- Paso 1: documentos (opcional) */}
      {key === 'documentos' && (
        <QuestionScreen
          step={stepLabel}
          question="Arrastra los documentos del cliente"
          helper="Opcional: suelta todo el KYC de una vez (cédula, formulario, pacto social…) y el registro se llena solo. También puedes continuar sin documentos."
          primaryLabel={
            intakeBusy ? 'Leyendo…' : intakeFiles.length > 0 ? 'Continuar' : 'Continuar sin documentos'
          }
          onPrimary={next}
          primaryDisabled={intakeBusy}
        >
          <div className="grid gap-3">
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIntakeDrag(true);
              }}
              onDragLeave={() => setIntakeDrag(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIntakeDrag(false);
                void ingestIntake(e.dataTransfer.files);
              }}
              className={cn(
                'grid place-items-center gap-1 rounded-2xl border-2 border-dashed px-4 py-8 text-center transition-colors',
                intakeDrag ? 'border-primary bg-primary/5' : 'border-line-strong bg-canvas'
              )}
            >
              <UploadCloud className="h-7 w-7 text-ink-faint" strokeWidth={1.75} />
              <p className="text-sm font-bold text-ink">Suelta aquí todos los documentos</p>
              <p className="text-sm text-ink-faint">
                Detectamos cada tipo por el nombre del archivo, leemos la cédula y
                llenamos el registro por ti.
              </p>
              <label className="mt-1 inline-flex cursor-pointer items-center gap-2 rounded-xl border-2 border-line-strong bg-panel px-3.5 py-2 text-sm font-bold text-ink-soft transition-colors hover:border-primary hover:text-primary-dark">
                <input
                  type="file"
                  accept={ALLOWED_DOCUMENT_MIME_TYPES.join(',')}
                  multiple
                  className="sr-only"
                  onChange={(e) => {
                    if (e.target.files) void ingestIntake(e.target.files);
                    e.target.value = '';
                  }}
                />
                <Paperclip className="h-4 w-4" strokeWidth={1.75} /> Elegir archivos
              </label>
            </div>

            {/* Lo autollenado, visible en la misma pantalla. */}
            {intakeOcrDone && name !== '' && (
              <p className="rounded-xl border border-primary/40 bg-primary/5 px-4 py-3 text-sm text-ink">
                Identidad leída de la cédula: <b>{name}</b>
                {docId ? <> · <b>{docId}</b></> : null}
                {birthDate ? <> · nació {birthDate}</> : null}
              </p>
            )}
            {intakeBusy && (
              <p className="flex items-center gap-2 rounded-xl border border-line bg-panel px-4 py-3 text-sm text-ink-soft">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-line-strong border-t-primary" />
                Leyendo tu documento…
              </p>
            )}
            {intakeFiles.length > 0 && !isPn && (
              <p className="rounded-xl border border-line bg-panel px-4 py-3 text-sm text-ink-soft">
                Por los documentos, parece{' '}
                <b className="text-ink">{isFundacion ? 'una Fundación de Interés Privado' : 'una Sociedad Anónima'}</b>{' '}
                — confírmalo en el siguiente paso.
              </p>
            )}

            {intakeFiles.map((it) => (
              <div
                key={it.id}
                className={cn(
                  'rounded-2xl border-2 bg-panel p-4',
                  it.dt ? 'border-primary/40' : 'border-dashed border-tone-amber/50'
                )}
              >
                <div className="flex items-center gap-2">
                  {it.dt ? (
                    <FileCheck2 className="h-5 w-5 shrink-0 text-primary-dark" strokeWidth={1.75} />
                  ) : (
                    <FileQuestion className="h-5 w-5 shrink-0 text-tone-amber" strokeWidth={1.75} />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-ink" title={it.file.name}>
                      {it.file.name}
                    </p>
                    <p className="text-sm text-ink-faint">
                      {it.dt ? DOC_TYPE_LABELS[it.dt] : 'Sin clasificar — elige el tipo:'}
                    </p>
                  </div>
                  <button
                    type="button"
                    aria-label={`Quitar ${it.file.name}`}
                    onClick={() => removeIntake(it.id)}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-ink-faint transition-colors hover:bg-canvas hover:text-ink"
                  >
                    <X className="h-4 w-4" strokeWidth={2} />
                  </button>
                </div>
                {!it.dt && (
                  <div className="mt-2">
                    <FlowSelect
                      options={INTAKE_TYPE_OPTS}
                      placeholder="Tipo de documento…"
                      value=""
                      onChange={(e) => {
                        const dt = e.target.value as DocTypeKey;
                        if (dt) reassignIntake(it.id, dt);
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </QuestionScreen>
      )}

      {/* ---------------------------------------------------- Paso 2: tipo */}
      {key === 'tipo' && (
        <QuestionScreen
          step={stepLabel}
          question="¿Qué tipo de cliente vas a registrar?"
          helper="Cambia los datos, los roles y los documentos que pediremos."
          primaryLabel="Continuar"
          onPrimary={next}
        >
          <div className="grid gap-3">
            <ChoiceCard
              icon={User}
              label="Persona Natural"
              sub="Un cliente individual"
              selected={entityType === 'persona_natural'}
              onClick={() => setEntityType('persona_natural')}
            />
            <ChoiceCard
              icon={Building2}
              label="Sociedad Anónima"
              sub="Empresa con junta directiva y accionistas"
              selected={entityType === 'sociedad_anonima'}
              onClick={() => setEntityType('sociedad_anonima')}
            />
            <ChoiceCard
              icon={Landmark}
              label="Fundación de Interés Privado"
              sub="Patrimonio con fundador y consejo fundacional"
              selected={entityType === 'fundacion'}
              onClick={() => setEntityType('fundacion')}
            />
          </div>
        </QuestionScreen>
      )}

      {/* ---------------------------------------------------- PN: escanear */}
      {key === 'escanear' && (
        <QuestionScreen
          step={stepLabel}
          question={t('intakeScan.title')}
          helper={t('intakeScan.help')}
          primaryLabel={t('intakeScan.skip')}
          onPrimary={next}
          primaryDisabled={scanBusy}
        >
          <div className="grid gap-4">
            <label
              onDragOver={(e) => {
                e.preventDefault();
                setScanDragOver(true);
              }}
              onDragLeave={() => setScanDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setScanDragOver(false);
                const file = e.dataTransfer.files[0];
                if (file && !scanBusy) void handleScan(file);
              }}
              className={scanPickerCn(
                cn(
                  'grid cursor-pointer place-items-center gap-1 rounded-xl border-2 border-dashed px-4 py-7 text-center transition-colors hover:border-primary',
                  scanDragOver ? 'border-primary bg-primary/5' : 'border-line-strong bg-canvas'
                )
              )}
            >
              <input
                type="file"
                accept="image/*,.pdf"
                className="sr-only"
                disabled={scanBusy}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleScan(file);
                  e.target.value = '';
                }}
              />
              {scanBusy ? (
                <>
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-line-strong border-t-primary" />
                  <span className="text-sm font-semibold text-ink-soft">{t('intakeScan.processing')}</span>
                </>
              ) : (
                <>
                  <Paperclip className="h-6 w-6 text-ink-faint" strokeWidth={1.75} />
                  <span className="text-sm font-semibold text-ink-soft">Arrastra un documento o haz clic</span>
                </>
              )}
            </label>
            <label
              className={scanPickerCn(
                'flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-line-strong bg-panel py-3.5 text-sm font-bold text-ink-soft transition-colors hover:border-primary hover:text-primary-dark'
              )}
            >
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="sr-only"
                disabled={scanBusy}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleScan(file);
                  e.target.value = '';
                }}
              />
              <Camera className="h-5 w-5" strokeWidth={1.75} /> Tomar foto
            </label>
            {scanError && (
              <p className="rounded-xl border border-tone-red/30 bg-tone-red-bg px-4 py-3 text-sm text-tone-red">
                {t(scanError === 'tooLarge' ? 'intakeScan.tooLarge' : 'intakeScan.error')}
              </p>
            )}
          </div>
        </QuestionScreen>
      )}

      {/* ---------------------------------------------------- PN: datos */}
      {key === 'datosPn' && (
        <QuestionScreen
          step={stepLabel}
          question={scanHint ? t('intake.confirmTitle') : t('intake.datosTitle')}
          helper={scanHint ? t('intake.confirmHelp') : t('intake.datosHelp')}
          primaryLabel="Continuar"
          onPrimary={next}
          primaryDisabled={!canNext}
        >
          <div className="grid gap-4">
            <Field label={t('intake.nameLabel')}>
              <BigInput
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={onEnter}
                placeholder="Nombre y apellidos"
              />
            </Field>
            <Field label="País de ciudadanía">
              <select
                value={country}
                onChange={(e) => {
                  const next = e.target.value;
                  setCountry(next);
                  setDocId((current) => formatIdNumber(next, current));
                }}
                className="h-16 w-full rounded-2xl border-2 border-line-strong bg-panel px-5 text-xl text-ink shadow-card focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/15"
              >
                {SUPPORTED_ID_COUNTRIES.map((c) => (
                  <option key={c.iso3} value={c.iso3}>
                    {c.labelEs}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('intake.cedulaLabel')}>
              <BigInput
                value={docId}
                onChange={(e) => setDocId(formatIdNumber(country, e.target.value))}
                onKeyDown={onEnter}
                placeholder={idNumberExample(country)}
              />
              {dup ? (
                <div className="mt-3 rounded-xl border border-tone-red/30 bg-tone-red-bg px-4 py-3 text-sm text-tone-red">
                  Ya existe un cliente con esa cédula: <b>{dup.name}</b>.{' '}
                  <button onClick={() => navigate(`/expedientes/${dup.id}`)} className="font-bold underline">
                    Ver expediente
                  </button>
                </div>
              ) : docHint ? (
                <p className="mt-3 text-sm text-ink-faint">
                  Revisa el formato — debe parecerse a {idNumberExample(country)}.
                </p>
              ) : null}
            </Field>
            <Field label={t('expedientes.birthDate')}>
              <BigInput type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
            </Field>
            <Field label="Correo (opcional)">
              <BigInput
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="correo@ejemplo.com"
              />
              {emailInvalid && (
                <p className="mt-2 text-sm text-tone-red">Revisa el correo — debe verse como correo@ejemplo.com.</p>
              )}
            </Field>
            <Field label="Teléfono (opcional)">
              <BigInput
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+507 6000-0000"
              />
            </Field>
          </div>
          {prefillHint}
        </QuestionScreen>
      )}

      {/* ---------------------------------------------------- PN: KYC */}
      {key === 'kycPn' && (
        <QuestionScreen
          step={stepLabel}
          question="Conozca a su Cliente"
          helper="Estos datos alimentan el cálculo de riesgo. Toca la ⓘ para entender cada campo."
          primaryLabel="Continuar"
          onPrimary={next}
          primaryDisabled={!canNext}
        >
          <div className="grid gap-5">
            <div>
              <FieldLabel text="Situación laboral" />
              <div className="grid gap-3">
                {PERFIL.map((p) => (
                  <ChoiceCard
                    key={p.value}
                    icon={PERFIL_ICON[p.value as keyof typeof PERFIL_ICON]}
                    label={p.label}
                    selected={perfil === p.value}
                    onClick={() => setPerfil(p.value)}
                  />
                ))}
              </div>
            </div>
            <SelectField
              label="Propósito de la relación"
              value={proposito}
              onChange={setProposito}
              options={PROPOSITO_OPTS}
            />
            <SelectField
              label="Profesión / actividad económica"
              help={RISK_HELP['cliente.profesion']}
              value={profesion}
              onChange={setProfesion}
              options={PROFESION_OPTS}
            />
            <SelectField
              label="Fuente de fondos"
              value={fuenteFondos}
              onChange={setFuenteFondos}
              options={FUENTE_FONDOS_OPTS}
            />
            <SelectField
              label="Nacionalidad"
              help={RISK_HELP['jurisdiccion.nacionalidad']}
              value={nacionalidad}
              onChange={setNacionalidad}
              options={NACIONALIDAD_OPTS}
            />
            <SelectField
              label="País de residencia fiscal"
              value={paisResidenciaFiscal}
              onChange={setPaisResidenciaFiscal}
              options={NACIONALIDAD_OPTS}
            />
            <SelectField
              label="País de domicilio / operación"
              help={RISK_HELP['jurisdiccion.domicilio']}
              value={paisDomicilio}
              onChange={setPaisDomicilio}
              options={DOMICILIO_OPTS}
            />
            <ToggleField
              label="¿Persona expuesta políticamente (PEP)?"
              help="Cargo público de alto nivel, propio o de un familiar/allegado (Ley 23 Art. 27.6)."
              value={pepPropio}
              onChange={setPepPropio}
            />
            <ToggleField
              label="¿Familiar o allegado de un PEP?"
              value={pepFamiliar}
              onChange={setPepFamiliar}
            />
            <ToggleField label="¿Es US person (FATCA)?" value={usPerson} onChange={setUsPerson} />
            <ToggleField
              label="¿Tiene múltiples nacionalidades?"
              value={multiplesNacionalidades}
              onChange={setMultiplesNacionalidades}
            />
          </div>
        </QuestionScreen>
      )}

      {/* ---------------------------------------------------- PN: canal / producto */}
      {key === 'canalPn' && (
        <QuestionScreen
          step={stepLabel}
          question="Canal y producto"
          helper="Cómo llega el cliente y qué producto contrata — factores de la matriz."
          primaryLabel="Continuar"
          onPrimary={next}
          primaryDisabled={!canNext}
        >
          <div className="grid gap-5">
            <SelectField
              label="Canal de onboarding"
              help={RISK_HELP['canal.canal_via']}
              value={canalVia}
              onChange={setCanalVia}
              options={CANAL_OPTS}
            />
            {showProducto && (
              <SelectField
                label="Producto / servicio"
                help={RISK_HELP['producto']}
                value={producto}
                onChange={setProducto}
                options={PRODUCTO_OPTS}
              />
            )}
            {!showProducto && (
              <p className="rounded-xl border border-line bg-panel px-4 py-3 text-sm text-ink-soft">
                Este sector no pondera producto ni monto/frecuencia/instrumento; solo el canal.
              </p>
            )}
          </div>
        </QuestionScreen>
      )}

      {/* ---------------------------------------------------- domicilio (PN + PJ) */}
      {key === 'domicilio' && (
        <QuestionScreen
          step={stepLabel}
          question="Domicilio"
          helper="Escanea un recibo de servicio para autocompletar la dirección, o escríbela a mano."
          primaryLabel="Continuar"
          onPrimary={next}
          primaryDisabled={!canNext}
        >
          <div className="grid gap-4">
            <label
              className={cn(
                'grid cursor-pointer place-items-center gap-1 rounded-xl border-2 border-dashed border-line-strong bg-canvas px-4 py-7 text-center transition-colors hover:border-primary',
                reciboBusy && 'pointer-events-none opacity-60'
              )}
            >
              <input
                type="file"
                accept="image/*,.pdf"
                className="sr-only"
                disabled={reciboBusy}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleReciboScan(file);
                  e.target.value = '';
                }}
              />
              {reciboBusy ? (
                <>
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-line-strong border-t-primary" />
                  <span className="text-sm font-semibold text-ink-soft">{t('intakeScan.processing')}</span>
                </>
              ) : (
                <>
                  <Paperclip className="h-6 w-6 text-ink-faint" strokeWidth={1.75} />
                  <span className="text-sm font-semibold text-ink-soft">Arrastra un recibo de servicio o haz clic</span>
                </>
              )}
            </label>
            <label
              className={cn(
                'flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-line-strong bg-panel py-3.5 text-sm font-bold text-ink-soft transition-colors hover:border-primary hover:text-primary-dark',
                reciboBusy && 'pointer-events-none opacity-60'
              )}
            >
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="sr-only"
                disabled={reciboBusy}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleReciboScan(file);
                  e.target.value = '';
                }}
              />
              <Camera className="h-5 w-5" strokeWidth={1.75} /> Tomar foto
            </label>
            {reciboError && (
              <p className="rounded-xl border border-tone-red/30 bg-tone-red-bg px-4 py-3 text-sm text-tone-red">
                {t(reciboError === 'tooLarge' ? 'intakeScan.tooLarge' : 'intakeScan.error')}
              </p>
            )}
            <Field label="Domicilio">
              <BigInput
                value={direccion}
                onChange={(e) => setDireccion(e.target.value)}
                onKeyDown={onEnter}
                placeholder="Calle, edificio, ciudad"
              />
            </Field>
          </div>
        </QuestionScreen>
      )}

      {/* ---------------------------------------------------- PN: documentos */}
      {key === 'docsPn' && (
        <QuestionScreen
          step={stepLabel}
          question="Documentos del cliente"
          helper="Adjunta lo que tengas ahora. Puedes completar los faltantes después."
          primaryLabel="Continuar"
          onPrimary={next}
        >
          <div className="grid gap-3">
            <p className="rounded-xl border border-line bg-panel px-4 py-3 text-sm text-ink-soft">
              Plantilla KYC:{' '}
              <b className="text-ink">
                {sectorLabel} · {plantilla.nombre}
              </b>
            </p>
            <DocSlots
              slots={plantilla.docs}
              attached={fileNames(entityDocs)}
              onAttach={(file, dt) => setEntityDocs((prev) => ({ ...prev, [dt]: file }))}
            />
          </div>
        </QuestionScreen>
      )}

      {/* ---------------------------------------------------- PJ: datos entidad */}
      {key === 'datosPj' && (
        <QuestionScreen
          step={stepLabel}
          question={isFundacion ? 'Datos de la fundación' : 'Datos de la sociedad'}
          helper="Los datos de la entidad. Las personas se agregan en el siguiente paso."
          primaryLabel="Continuar"
          onPrimary={next}
          primaryDisabled={!canNext}
        >
          <div className="grid gap-4">
            <Field label="Razón social">
              <BigInput
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={isFundacion ? 'Nombre de la fundación' : 'Nombre de la empresa'}
              />
            </Field>
            <Field label="RUC">
              <BigInput
                value={docId}
                onChange={(e) => setDocId(formatRuc(e.target.value))}
                placeholder="155-2026-xxx"
              />
              {dup ? (
                <div className="mt-3 rounded-xl border border-tone-red/30 bg-tone-red-bg px-4 py-3 text-sm text-tone-red">
                  Ya existe un cliente con ese RUC: <b>{dup.name}</b>.{' '}
                  <button onClick={() => navigate(`/expedientes/${dup.id}`)} className="font-bold underline">
                    Ver expediente
                  </button>
                </div>
              ) : docHint ? (
                <p className="mt-3 text-sm text-ink-faint">Revisa el formato del RUC.</p>
              ) : null}
            </Field>
            <SelectField
              label="Propósito de la relación"
              value={proposito}
              onChange={setProposito}
              options={PROPOSITO_OPTS}
            />
            <SelectField
              label="País de constitución"
              help={RISK_HELP['jurisdiccion.nacionalidad']}
              value={nacionalidad}
              onChange={setNacionalidad}
              options={NACIONALIDAD_OPTS}
            />
            <SelectField
              label="País de domicilio / operación"
              help={RISK_HELP['jurisdiccion.domicilio']}
              value={paisDomicilio}
              onChange={setPaisDomicilio}
              options={DOMICILIO_OPTS}
            />
            <MultiCountryField
              label="Países de actividad"
              pick={paisActividadPick}
              onPick={setPaisActividadPick}
              values={paisesActividad}
              onAdd={(v) =>
                setPaisesActividad((prev) => (prev.includes(v) ? prev : [...prev, v]))
              }
              onRemove={(v) => setPaisesActividad((prev) => prev.filter((x) => x !== v))}
            />
            <SelectField
              label="Fuente de fondos"
              value={fuenteFondos}
              onChange={setFuenteFondos}
              options={FUENTE_FONDOS_OPTS}
            />
            <Field label="No. de registro / folio">
              <BigInput value={folio} onChange={(e) => setFolio(e.target.value)} placeholder="Folio del Registro Público" />
            </Field>
            <Field label={isFundacion ? 'Fecha de fundación' : 'Fecha de constitución'}>
              <BigInput type="date" value={constitutionDate} onChange={(e) => setConstitutionDate(e.target.value)} />
            </Field>
          </div>
        </QuestionScreen>
      )}

      {/* ---------------------------------------------------- PJ: personas */}
      {key === 'personas' && (
        <QuestionScreen
          step={stepLabel}
          question={editorPerson ? 'Datos de la persona' : 'Personas de la estructura'}
          helper={
            editorPerson
              ? 'Rol, identidad y documentos de esta persona.'
              : isFundacion
                ? 'Fundador, consejo fundacional y beneficiarios. Un beneficiario secundario se registra sin documentación.'
                : 'Junta directiva, accionistas y beneficiarios finales. Un accionista sobre el umbral se marca como beneficiario final.'
          }
          primaryLabel={primaryLabelPersonas}
          onPrimary={editorPerson ? savePerson : next}
          primaryDisabled={primaryDisabledPersonas}
          secondary={
            editorPerson ? (
              <button
                type="button"
                onClick={() => {
                  setEditorPerson(null);
                  setEditorDocs({});
                }}
                className="w-full rounded-2xl py-3 text-sm font-bold text-ink-soft hover:bg-panel"
              >
                Cancelar
              </button>
            ) : undefined
          }
        >
          {editorPerson ? (
            <PersonEditor
              person={editorPerson}
              docs={editorDocs}
              sector={sector}
              entityType={entityType}
              roleOptions={ROLE_OPTS}
              onPatch={patchEditor}
              onAttach={attachEditorDoc}
            />
          ) : (
            <div className="grid gap-3">
              {people.map((p) => (
                <PersonCard
                  key={p.localId}
                  person={p}
                  sector={sector}
                  entityType={entityType}
                  roleOptions={ROLE_OPTS}
                  onEdit={() => openEditPerson(p)}
                  onRemove={() => removePerson(p.localId)}
                />
              ))}
              <button
                type="button"
                onClick={openNewPerson}
                className="flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-line-strong bg-panel py-4 text-sm font-bold text-primary-dark transition-colors hover:border-primary"
              >
                <Plus className="h-5 w-5" strokeWidth={2} /> Agregar persona
              </button>
              {people.length === 0 && (
                <p className="text-center text-sm text-ink-faint">
                  Agrega al menos una persona de la estructura para continuar.
                </p>
              )}
            </div>
          )}
        </QuestionScreen>
      )}

      {/* ---------------------------------------------------- PJ: riesgo entidad */}
      {key === 'riesgoPj' && (
        <QuestionScreen
          step={stepLabel}
          question="Canal y producto de la entidad"
          helper="Cómo se relaciona la entidad y qué producto contrata — factores de la matriz."
          primaryLabel="Continuar"
          onPrimary={next}
          primaryDisabled={!canNext}
        >
          <div className="grid gap-5">
            <SelectField
              label="Canal de onboarding"
              help={RISK_HELP['canal.canal_via']}
              value={canalVia}
              onChange={setCanalVia}
              options={CANAL_OPTS}
            />
            {showProducto && (
              <SelectField
                label="Producto / servicio"
                help={RISK_HELP['producto']}
                value={producto}
                onChange={setProducto}
                options={PRODUCTO_OPTS}
              />
            )}
            {!showProducto && (
              <p className="rounded-xl border border-line bg-panel px-4 py-3 text-sm text-ink-soft">
                Este sector no pondera producto ni monto/frecuencia/instrumento; solo el canal.
              </p>
            )}
          </div>
        </QuestionScreen>
      )}

      {/* ---------------------------------------------------- PJ: documentos entidad */}
      {key === 'docsPj' && (
        <QuestionScreen
          step={stepLabel}
          question="Documentos de la entidad"
          helper="Los documentos de la sociedad o fundación. Los de cada persona ya quedaron en su ficha."
          primaryLabel="Continuar"
          onPrimary={next}
        >
          <div className="grid gap-3">
            <p className="rounded-xl border border-line bg-panel px-4 py-3 text-sm text-ink-soft">
              Plantilla KYC:{' '}
              <b className="text-ink">
                {sectorLabel} · {plantilla.nombre}
              </b>
            </p>
            <DocSlots
              slots={plantilla.docs}
              attached={fileNames(entityDocs)}
              onAttach={(file, dt) => setEntityDocs((prev) => ({ ...prev, [dt]: file }))}
            />
          </div>
        </QuestionScreen>
      )}

      {/* ---------------------------------------------------- revisión */}
      {key === 'revision' && (
        <QuestionScreen
          step={stepLabel}
          question="Revisa antes de guardar"
          helper="Si todo está bien, crea el cliente."
          primaryLabel={busy ? 'Guardando…' : 'Crear cliente'}
          onPrimary={submit}
          primaryDisabled={busy}
        >
          <div className="grid gap-5">
            <dl className="divide-y divide-line rounded-2xl border border-line bg-panel px-5 shadow-card">
              <Row label="Sector" value={sectorLabel} />
              <Row
                label="Tipo de cliente"
                value={isPn ? 'Persona Natural' : isFundacion ? 'Fundación' : 'Sociedad Anónima'}
              />
              <Row label={isPn ? 'Nombre' : 'Razón social'} value={name || '—'} />
              <Row label={isPn ? 'Cédula' : 'RUC'} value={docId || '—'} />
              {isPn && <Row label={t('expedientes.birthDate')} value={birthDate || '—'} />}
              <Row label="Domicilio" value={direccion || '—'} />
              <Row label="Propósito" value={proposito ? labelOf(PROPOSITO_OPTS, proposito) : '—'} />
              {isPn && (
                <Row label="Profesión" value={profesion ? labelOf(PROFESION_OPTS, profesion) : '—'} />
              )}
              <Row label="Fuente de fondos" value={fuenteFondos ? labelOf(FUENTE_FONDOS_OPTS, fuenteFondos) : '—'} />
              <Row
                label={isPn ? 'Nacionalidad' : 'País de constitución'}
                value={nacionalidad ? labelOf(NACIONALIDAD_OPTS, nacionalidad) : '—'}
              />
              <Row label="Canal" value={canalVia ? labelOf(CANAL_OPTS, canalVia) : '—'} />
              {showProducto && <Row label="Producto" value={producto ? labelOf(PRODUCTO_OPTS, producto) : '—'} />}
              {!isPn && <Row label="Personas" value={`${people.length}`} />}
              <Row label="PEP" value={pepOverride ? 'Sí' : 'No'} />
            </dl>

            {!isPn && (
              <div className="rounded-2xl border border-line bg-panel p-5 shadow-card">
                <div className="mb-3 flex items-center gap-2">
                  <ScrollText className="h-5 w-5 text-primary-dark" strokeWidth={1.75} />
                  <h3 className="font-serif text-lg font-medium text-ink">Declaración Jurada de Beneficiario Final</h3>
                </div>
                {beneficiarios.length === 0 ? (
                  <p className="text-sm text-ink-faint">
                    Aún no hay beneficiarios finales marcados en la estructura.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[22rem] text-sm">
                      <thead>
                        <tr className="text-left text-ink-faint">
                          <th className="pb-2 pr-3 font-semibold">Beneficiario</th>
                          <th className="pb-2 pr-3 font-semibold">Residencia fiscal</th>
                          <th className="pb-2 font-semibold">% patrimonial</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-line">
                        {beneficiarios.map((p) => (
                          <tr key={p.localId}>
                            <td className="py-2 pr-3 font-semibold text-ink">{p.name || '—'}</td>
                            <td className="py-2 pr-3 text-ink-soft">
                              {p.paisResidenciaFiscal ? labelOf(NACIONALIDAD_OPTS, p.paisResidenciaFiscal) : '—'}
                            </td>
                            <td className="py-2 text-ink-soft">
                              {p.porcentajePatrimonial ? `${p.porcentajePatrimonial}%` : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="mt-3 text-xs text-ink-faint">
                  La declaración se firmará al finalizar el registro.
                </p>
              </div>
            )}
          </div>
          {prefillHint}
        </QuestionScreen>
      )}
    </FlowShell>
  );
}

// ---------------------------------------------------------------- pieces ----

function fileNames(m: PersonDocMap): Partial<Record<DocTypeKey, string>> {
  const out: Partial<Record<DocTypeKey, string>> = {};
  for (const dt of Object.keys(m) as DocTypeKey[]) {
    const f = m[dt];
    if (f) out[dt] = f.name;
  }
  return out;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3.5">
      <dt className="text-sm text-ink-faint">{label}</dt>
      <dd className="text-right font-semibold text-ink">{value}</dd>
    </div>
  );
}

function FieldLabel({ text, help }: { text: string; help?: string | undefined }) {
  return (
    <span className="mb-1.5 flex items-center gap-1.5 text-sm font-bold text-ink-soft">
      {text}
      {help && <FieldHelp text={help} label={`Qué significa: ${text}`} />}
    </span>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-bold text-ink-soft">{label}</span>
      {children}
      {hint && <p className="mt-1.5 text-sm text-ink-faint">{hint}</p>}
    </label>
  );
}

function SelectField({
  label,
  help,
  value,
  onChange,
  options,
}: {
  label: string;
  help?: string | undefined;
  value: string;
  onChange: (v: string) => void;
  options: FlowOption[];
}) {
  return (
    <div>
      <FieldLabel text={label} help={help} />
      <FlowSelect
        options={options}
        placeholder="Elige…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function ToggleField({
  label,
  help,
  value,
  onChange,
}: {
  label: string;
  help?: string | undefined;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-1.5 text-sm font-bold text-ink-soft">
        {label}
        {help && <FieldHelp text={help} label={`Qué significa: ${label}`} />}
      </span>
      <Toggle value={value} onChange={onChange} />
    </div>
  );
}

function MultiCountryField({
  label,
  pick,
  onPick,
  values,
  onAdd,
  onRemove,
}: {
  label: string;
  pick: string;
  onPick: (v: string) => void;
  values: string[];
  onAdd: (v: string) => void;
  onRemove: (v: string) => void;
}) {
  return (
    <div>
      <FieldLabel text={label} />
      <div className="flex gap-2">
        <div className="flex-1">
          <FlowSelect
            options={NACIONALIDAD_OPTS}
            placeholder="Elige un país…"
            value={pick}
            onChange={(e) => onPick(e.target.value)}
          />
        </div>
        <button
          type="button"
          disabled={!pick}
          onClick={() => {
            if (pick) {
              onAdd(pick);
              onPick('');
            }
          }}
          className="shrink-0 rounded-2xl border-2 border-line-strong bg-panel px-4 text-sm font-bold text-ink-soft transition-colors hover:border-primary hover:text-primary-dark disabled:pointer-events-none disabled:opacity-40"
        >
          Agregar
        </button>
      </div>
      {values.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {values.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => onRemove(v)}
              className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/8 px-3 py-1.5 text-sm font-semibold text-primary-dark"
            >
              {COUNTRY_LABEL.get(v) ?? v}
              <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function personSlots(p: PersonForm, sector: EntityMode, entityType: EntityType): DocTypeKey[] {
  return requiredDocsForPerson({
    sector,
    entityType,
    rol: p.rol,
    usPerson: p.usPerson,
    ...(p.posicion ? { posicion: p.posicion } : {}),
  });
}

function PersonCard({
  person,
  sector,
  entityType,
  roleOptions,
  onEdit,
  onRemove,
}: {
  person: PersonForm;
  sector: EntityMode;
  entityType: EntityType;
  roleOptions: FlowOption[];
  onEdit: () => void;
  onRemove: () => void;
}) {
  const isUbo =
    entityType === 'sociedad_anonima' &&
    person.rol === 'accionista' &&
    isUboByOwnership(Number(person.ownershipPct) || 0, sector);
  const slots = personSlots(person, sector, entityType);
  const done = slots.length === 0;
  return (
    <div className="rounded-2xl border-2 border-line-strong bg-panel p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-bold text-ink">{person.name || 'Sin nombre'}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className="rounded-full bg-canvas px-2.5 py-0.5 text-[11px] font-bold text-ink-soft">
              {labelOf(roleOptions, person.rol)}
            </span>
            {person.rol === 'accionista' && person.ownershipPct && (
              <span className="rounded-full bg-canvas px-2.5 py-0.5 text-[11px] font-bold text-ink-soft">
                {person.ownershipPct}%
              </span>
            )}
            {isUbo && (
              <span className="rounded-full bg-primary/12 px-2.5 py-0.5 text-[11px] font-bold text-primary-dark">
                Beneficiario final
              </span>
            )}
            {person.posicion && (
              <span className="rounded-full bg-canvas px-2.5 py-0.5 text-[11px] font-bold text-ink-soft">
                {person.posicion === 'secundario' ? 'Secundario' : 'Primario'}
              </span>
            )}
          </div>
          <p className="mt-1.5 text-xs text-ink-faint">
            {done ? 'Sin requisitos documentales' : `${slots.length} documento(s) requerido(s)`}
          </p>
        </div>
        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            onClick={onEdit}
            aria-label="Editar persona"
            className="grid h-9 w-9 place-items-center rounded-xl text-ink-soft hover:bg-canvas hover:text-primary-dark"
          >
            <Pencil className="h-4 w-4" strokeWidth={1.75} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            aria-label="Quitar persona"
            className="grid h-9 w-9 place-items-center rounded-xl text-ink-soft hover:bg-canvas hover:text-tone-red"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>
    </div>
  );
}

function PersonEditor({
  person,
  docs,
  sector,
  entityType,
  roleOptions,
  onPatch,
  onAttach,
}: {
  person: PersonForm;
  docs: PersonDocMap;
  sector: EntityMode;
  entityType: EntityType;
  roleOptions: FlowOption[];
  onPatch: (patch: Partial<PersonForm>) => void;
  onAttach: (file: File, dt: DocTypeKey) => void;
}) {
  const isFundacionBeneficiario =
    entityType === 'fundacion' && person.rol === 'beneficiario_final';
  const isSecundario = isFundacionBeneficiario && person.posicion === 'secundario';
  const isUbo =
    entityType === 'sociedad_anonima' &&
    person.rol === 'accionista' &&
    isUboByOwnership(Number(person.ownershipPct) || 0, sector);
  const slots = personSlots(person, sector, entityType);

  return (
    <div className="grid gap-5">
      <SelectField
        label="Rol en la estructura"
        value={person.rol}
        onChange={(v) => onPatch({ rol: v as OwnerRol })}
        options={roleOptions}
      />

      {isFundacionBeneficiario && (
        <SelectField
          label="Posición del beneficiario"
          help="La fundación es patrimonial: al beneficiario secundario no se le pide documentación (criterio por posición, no por porcentaje)."
          value={person.posicion}
          onChange={(v) => onPatch({ posicion: v as OwnerPosicion })}
          options={POSICION_OPTS}
        />
      )}

      <Field label="Nombre completo">
        <BigInput
          value={person.name}
          onChange={(e) => onPatch({ name: e.target.value })}
          placeholder="Nombre y apellidos"
        />
      </Field>

      {isSecundario ? (
        <div className="rounded-2xl border border-line bg-panel px-4 py-3 text-sm text-ink-soft">
          Beneficiario secundario: sin requisitos documentales.
        </div>
      ) : (
        <>
          <SelectField
            label="Tipo de documento"
            value={person.tipoDocumento}
            onChange={(v) =>
              onPatch({ tipoDocumento: v === 'pasaporte' ? 'pasaporte' : 'cedula' })
            }
            options={TIPO_DOC_OPTS}
          />
          <Field label="No. de documento">
            <BigInput
              value={person.numeroDocumento}
              onChange={(e) =>
                onPatch({
                  numeroDocumento:
                    person.tipoDocumento === 'pasaporte'
                      ? e.target.value
                      : formatIdNumber(iso2ToIso3(person.nacionalidad) ?? 'PAN', e.target.value),
                })
              }
              placeholder={
                person.tipoDocumento === 'pasaporte'
                  ? 'Pasaporte'
                  : idNumberExample(iso2ToIso3(person.nacionalidad) ?? 'PAN')
              }
            />
          </Field>
          <SelectField
            label="Nacionalidad"
            help={RISK_HELP['jurisdiccion.nacionalidad']}
            value={person.nacionalidad}
            onChange={(v) => onPatch({ nacionalidad: v })}
            options={NACIONALIDAD_OPTS}
          />
          <SelectField
            label="País de residencia fiscal"
            value={person.paisResidenciaFiscal}
            onChange={(v) => onPatch({ paisResidenciaFiscal: v })}
            options={NACIONALIDAD_OPTS}
          />
          {entityType === 'sociedad_anonima' && person.rol === 'accionista' && (
            <Field
              label="% de participación"
              hint={
                isUbo
                  ? `Sobre el umbral de ${UBO_THRESHOLD_BY_SECTOR[sector]}% → beneficiario final.`
                  : `Umbral de beneficiario final del sector: ${UBO_THRESHOLD_BY_SECTOR[sector]}%.`
              }
            >
              <BigInput
                inputMode="decimal"
                value={person.ownershipPct}
                onChange={(e) => onPatch({ ownershipPct: e.target.value.replace(/[^0-9.]/g, '') })}
                placeholder="0"
              />
            </Field>
          )}
          <Field label="% patrimonial">
            <BigInput
              inputMode="decimal"
              value={person.porcentajePatrimonial}
              onChange={(e) =>
                onPatch({ porcentajePatrimonial: e.target.value.replace(/[^0-9.]/g, '') })
              }
              placeholder="0"
            />
          </Field>
          <ToggleField
            label="¿Persona expuesta políticamente (PEP)?"
            value={person.pepPropio}
            onChange={(v) => onPatch({ pepPropio: v })}
          />
          <ToggleField
            label="¿Familiar o allegado de un PEP?"
            value={person.pepFamiliar}
            onChange={(v) => onPatch({ pepFamiliar: v })}
          />
          <ToggleField
            label="¿Es US person (FATCA)?"
            value={person.usPerson}
            onChange={(v) => onPatch({ usPerson: v })}
          />

          <div>
            <FieldLabel text="Documentos de esta persona" />
            <DocSlots
              slots={slots}
              attached={fileNames(docs)}
              onAttach={onAttach}
              emptyNote="Sin requisitos documentales para este rol."
            />
          </div>
        </>
      )}

      {isUbo && (
        <div className="flex items-center gap-2 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm font-semibold text-primary-dark">
          <Users className="h-4 w-4" strokeWidth={2} /> Se marcará como beneficiario final.
        </div>
      )}
    </div>
  );
}
